<template>
    <div class="grouped-bar-chart" :class="{ 'is-hovered': isHovered }">
      <div class="legend" :class="{ 'has-active-item': hasIsolations }">
        <div class="legend-item" v-for="(item, index) in keys" :key="index" @click="isolateItem(item)" :class="{ 'is-active': isIsolated(item) }" :style="{ 'background-color': getLegendItemColor(item) }">
          <span class="legend-item-dot" :style="{ 'background-color': getLegendItemDotColor(item) }"></span>
          <div class="legend-item-text">{{ item }}</div>
        </div>
      </div>
      <svg ref="svg" :width="width + margin.left + margin.right" :height="height + margin.top + margin.bottom">
        <g class="x axis" ref="xAxis" :style="{ 'transform': `translate(${margin.left}px, ${height + margin.top}px)`}">
          <g class="tick" v-for="(item, index) in categories" :key="index" :style="{ 'transform': `translate(${x0(item)}px, 0px)`}">
            <g v-if="icons[item]">
              <text :style="{ 'transform': `translate(${x0.step() / 2}px, 0px)` }" y="26">
                <tspan class="material-icons" :class="icons[item]">{{ icons[item] }}</tspan>
                <tspan class="blokk">-</tspan>
                <tspan class="text">{{ item }}</tspan>
              </text>
            </g>
            <g v-else>
              <text :style="{ 'transform': `translate(${x0.step() / 2}px, 0px)` }" y="26">
                <tspan class="text">
                  {{ item === 1 ? 'once/week' : item }}
                </tspan>
              </text>
            </g>
            <line :y2="-8" />
          </g>
          <g class="tick" :style="{ 'transform': `translate(${width}px, 0px)`}">
            <line :y2="-8" />
          </g>
        </g>
        <g class="y axis" ref="yAxis" :style="{ 'transform': `translate(0px, ${margin.top}px)`}"></g>
        <g ref="bars" :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)`}">
          <g class="bar-group" v-for="(item, index) in traces" :key="index" :style="{ 'transform': `translate(${x0(item.category) + x0.step() / 2 - x1.range()[1] / 2 + 3}px, 0px)`}">
            <g class="bar" v-for="(d, i) in item.values" :key="i" @mouseenter="mouseenter(d)" @mouseleave="mouseleave(d)" :class="{ 'is-active': d === tooltipItem }">
              <rect class="bar-rect" :x="x1(d.mode)" :y="y(d.value)" :height="getBarHeight(d.value) + 3.5" :fill="color(d.mode)" rx="2.5" ry="2.5" />
              <circle class="bar-dot" :cx="x1(d.mode) + 2.5" :cy="y(d.value) + 2.5" r="1.5" v-if="getBarHeight(d.value) > 5" />
            </g>
          </g>
          <rect :style="{ 'transform': `translate(0, ${height + 1}px)`}" :width="width" height="3" fill="#fff" />
        </g>
      </svg>
      <div class="tooltip" role="tooltip" ref="tooltip" v-if="tooltipItem">
        <div class="tooltip-inner">
          <div class="level">
            <div class="level-left item-type">
              <i class="material-icons" v-if="icons[tooltipItem.mode]">{{ icons[tooltipItem.mode] }}</i>
              {{ tooltipItem.mode }}
            </div>
            <div class="level-right item-value">{{ $t('charts.grouped_bar_chart.tooltip_users_amount', { amount: (tooltipItem.value * 100).toFixed(2) }) }}</div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { select, event, clientPoint } from 'd3-selection';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { axisBottom, axisLeft } from 'd3-axis';
import { max } from 'd3-array';
import { flow, map, flatten, uniqBy } from 'lodash/fp';
import { includes, filter, assignIn } from 'lodash';
import icons from '@/utils/icons';
import { resizable } from 'views/mixins'

export default {

  mixins: [resizable],

  props: {
    data: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      isolations: [],
      width: 0,
      height: 0,
      margin: { top: 24, right: 1, bottom: 27, left: 35 },
      x0: scaleBand(),
      x1: scaleBand(),
      y: scaleLinear(),
      yAxis: axisLeft(),
      color: scaleOrdinal(),
      tooltipItem: null,
      isHovered: false,
      icons: icons
    };
  },

  computed: {
    categories() {
      return map(d => d.category)(this.data);
    },

    keys() {
      return flow(
        map(d => d.values),
        flatten,
        uniqBy('mode'),
        map(d => d.mode)
      )(this.data);
    },

    traces() {
      if (!this.hasIsolations) {
        return this.data;
      }

      const traces = map(d => {
        return assignIn({}, d, {
          values: filter(d.values, item => {
            return this.isIsolated(item.mode);
          })
        });
      })(this.data);

      return traces;
    },

    hasIsolations() {
      return this.isolations.length > 0;
    }
  },

  mounted() {
    this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
    this.height = this.$el.getBoundingClientRect().height - this.margin.top - this.margin.bottom - 32;

    this.x0.rangeRound([0, this.width]);
  
    this.y.rangeRound([this.height, 0])
      .domain([0, 1]);

    this.colors = ['#000249', '#20367c', '#3c6bac', '#5ba6d4', '#aadcf4'];
    this.color.range(this.colors);

    this.yAxis.scale(this.y)
      .ticks(3)
      .tickFormat(d => d * 100);

    select(this.$refs.yAxis).call(this.yAxis);
    select(this.$refs.yAxis).select('.domain')
      .remove();

    const tickSize = select(this.$refs.yAxis).selectAll('.tick').size();

    select(this.$refs.yAxis).selectAll('.tick line')
      .attr('transform', `translate(${this.margin.left}, 0)`)
      .attr('x2', this.width);

    select(this.$refs.yAxis).selectAll('.tick text')
      .attr('x', 0)
      .text(function (d, i) {
        const tickText = select(this).text();

        if (tickSize - 1 === i) {
          select(this.parentNode).append('text')
            .text('users')
            .attr('y', 18.75);

          return `${tickText}%`;
        }

        return tickText;
      });

    select(this.$refs.bars)
      .on('mousemove', this.mousemove);

    if (this.data.length > 0) {
      this.update(this.data);
    }
  },

  methods: {
    onResize() {
      this.width = 0;

      this.$nextTick(() => {
        this.width = this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right;
        this.x0.rangeRound([0, this.width]);
      });
    },

    update(data) {
      const categories = this.categories;
      const keys = this.isolations.length > 0 ? filter(this.keys, key => this.isIsolated(key)) : this.keys;

      this.x0.domain(categories);
      this.x1.domain(keys)
        .rangeRound([0, keys.length * 5 + (keys.length - 1) * 8]);
    },

    getBarHeight(value) {
      if (this.height === 0) {
        return 0;
      }

      return this.height - this.y(value);
    },

    getStep(index) {
      const step = this.x0.step();
      
      return step * index;
    },

    isolateItem(item) {
      if (includes(this.isolations, item)) {
        this.isolations = filter(this.isolations, annotation => annotation !== item);
      } else {
        this.isolations = [...this.isolations, item];
      }

      this.update(this.data);
    },

    isIsolated(item) {
      return includes(this.isolations, item);
    },

    getLegendItemColor(item) {
      if (!includes(this.isolations, item)) {
        return;
      }

      return this.color(item);
    },

    getLegendItemDotColor(item) {
      if (includes(this.isolations, item)) {
        return;
      }

      return this.color(item);
    },

    mouseenter(item) {
      this.tooltipItem = item;
      this.isHovered = true;
    },

    mouseleave(item) {
      this.tooltipItem = null;
      this.isHovered = false;
    },

    mousemove() {
      const pos = clientPoint(this.$refs.bars, event);

      select(this.$refs.tooltip)
        .style('transform', `translate(${this.margin.left + pos[0]}px, ${this.margin.top + pos[1] + 15}px)`);
    }
  },

  watch: {
    data: function (newData) {
      this.data = newData;
      this.update(this.data);
    }
  }
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.grouped-bar-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  
  &.is-hovered {
    .tooltip {
      opacity: 1;
    }

    .bar {
      &:not(.is-active) {
        opacity: .5;
      }
    }
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    
    &.has-active-item {
      .legend-item {
        &:not(.is-active) {
          opacity: .4;

          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }

  .legend-item {
    display: flex;
    align-items: center;
    padding: .5rem;
    margin-right: .5rem;
    transform: translateX(-.625rem);
    border-radius: 1rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition: $transition;

    &:hover {
      border-color: $grey-lighter;
    }

    &:last-child {
      margin-right: 0;
    }

    &.is-active {
      border-color: transparent;

      .legend-item-text {
        color: $white;
        opacity: 1;
      }
    }
  }

  .legend-item-text {
    opacity: .64;
    font-size: .875rem;
    font-weight: $weight-medium;
    line-height: 1;
    color: $black-bis;
    opacity: .64;
    text-transform: capitalize;
    margin-left: .75rem;
    transition: $transition;
  }

  .legend-item-dot {
    width: .375rem;
    height: .375rem;
    background-color: $white;
    transform: rotate(45deg);
    transition: $transition;
  }

  .bar {
    transition: opacity .2s $timing-function;
  }

  .bar-group {
    // transition: transform .2s $timing-function;
  }

  .bar-rect {
    width: 5px;
    // transition: x .2s $timing-function;
  }

  .bar-dot {
    fill: $white;
    // transition: cx .2s $timing-function;
  }

  .axis {
    &.x {
      .domain {
        stroke-width: 1px;
        stroke: $grey-darker;
        opacity: .2;
      }

      .tick {
        text {
          font-size: $size-small;
          color: $black;
          text-transform: capitalize;
          text-anchor: middle;
          dominant-baseline: ideographic;
          
          .text {
            opacity: .64;
          }

          tspan {
            &.blokk {
              opacity: 0;
            }

            &.material-icons {
              font-size: .875rem;

              &.brightness_5 {
                fill: #f9cb00;
              }

              &.brightness_7 {
                fill: #e8843f;
              }

              &.brightness_medium {
                fill: #5ba6d4;
              }

              &.brightness_3 {
                fill: #20367c;
              }
            }
          }
        }

        line {
          stroke-width: 1px;
          stroke: $grey-darker;
          opacity: .2;
        }
      }
    }

    &.y {
      .tick {
        text {
          font-family: $family-sans-serif;
          font-size: $size-small;
          fill: $black-bis;  
          opacity: .64;
          text-anchor: start;
        }
        
        line {
          stroke-width: 1px;
          stroke: $grey-darker;
          opacity: .1;
        }
      }
    }
  }

  .tooltip {
    margin-left: 1rem;
    top: 1.25rem;
    left: 0;
    transition: none;

    .tooltip-inner {
      padding: .75rem;
    }

    .level-left {
      & + .level-right {
        margin-left: 1.75rem;
      }
    }

    .item-type {
      text-transform: capitalize;

      i {
        font-size: $size-small;
        margin-right: .5625rem;
      }
    }
  }
}
</style>
